import React, { useState, useEffect } from "react";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDown, Banknote, Tag, Loader2, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { getUniqueBrands, getUniqueCategories } from "@/services/productService";
import { useCurrency } from "@/context/CurrencyContext";

interface ProductFiltersProps {
  priceRange: [number, number];
  setPriceRange: (range: [number, number]) => void;
  selectedBrands: string[];
  setSelectedBrands: (brands: string[]) => void;
  selectedCategories: string[];
  setSelectedCategories: (cats: string[]) => void;
  maxPrice: number;
  onReset: () => void;
  className?: string;
}

const ProductFilters = ({
  priceRange,
  setPriceRange,
  selectedBrands,
  setSelectedBrands,
  selectedCategories,
  setSelectedCategories,
  maxPrice,
  onReset,
  className
}: ProductFiltersProps) => {
  const { formatPrice, selectedCurrency } = useCurrency();
  const [availableBrands, setAvailableBrands] = useState<string[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [brandsError, setBrandsError] = useState<string | null>(null);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  
  // Local state for debounced price range
  const [localPriceRange, setLocalPriceRange] = useState<[number, number]>(priceRange);
  
  // Sync local price range with prop changes
  useEffect(() => {
    setLocalPriceRange(priceRange);
  }, [priceRange]);
  
  // Debounced effect to update the actual price range
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (localPriceRange[0] !== priceRange[0] || localPriceRange[1] !== priceRange[1]) {
        setPriceRange(localPriceRange);
      }
    }, 800); // 800ms debounce delay
    
    return () => clearTimeout(timeoutId);
  }, [localPriceRange, priceRange, setPriceRange]);

  // Fetch brands when component mounts - only once due to global caching
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setBrandsLoading(true);
        setBrandsError(null);
        const brands = await getUniqueBrands();
        setAvailableBrands(brands);
      } catch (error) {
        console.error('Error fetching brands:', error);
        setBrandsError('Failed to load brands');
        setAvailableBrands([]); // No fallback to dummy data
      } finally {
        setBrandsLoading(false);
      }
    };

    fetchBrands();
  }, []); // Empty dependency array - fetch only once

  // Fetch categories once
  useEffect(() => {
    const fetchCats = async () => {
      try {
        setCategoriesLoading(true);
        setCategoriesError(null);
        const cats = await getUniqueCategories();
        setAvailableCategories(cats);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategoriesError('Failed to load categories');
        setAvailableCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCats();
  }, []);

  const handleBrandChange = (brand: string) => {
    if (selectedBrands.includes(brand)) {
      setSelectedBrands(selectedBrands.filter(b => b !== brand));
    } else {
      setSelectedBrands([...selectedBrands, brand]);
    }
  };

  const handleCategoryChange = (cat: string) => {
    if (selectedCategories.includes(cat)) {
      setSelectedCategories(selectedCategories.filter(c => c !== cat));
    } else {
      setSelectedCategories([...selectedCategories, cat]);
    }
  };

  const handleRetryBrands = async () => {
    try {
      setBrandsLoading(true);
      setBrandsError(null);
      const brands = await getUniqueBrands();
      setAvailableBrands(brands);
    } catch (error) {
      console.error('Error retrying brands fetch:', error);
      setBrandsError('Failed to load brands');
      setAvailableBrands([]);
    } finally {
      setBrandsLoading(false);
    }
  };

  // Check if price filter is active
  const isPriceFilterActive = priceRange[0] > 0 || priceRange[1] < maxPrice;

  return (
    <div className={cn("space-y-6", className)}>
      {/* Enhanced Filter Section Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-gray-800">Active Filters</h3>
          {(isPriceFilterActive || selectedBrands.length > 0) && (
            <Badge variant="outline" className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-yellow/10 text-jamaica-green border-jamaica-green/30 font-semibold text-xs shadow-sm">
              {(isPriceFilterActive ? 1 : 0) + selectedBrands.length}
            </Badge>
          )}
        </div>
        {(isPriceFilterActive || selectedBrands.length > 0) && (
        <Button 
            variant="ghost" 
          size="sm" 
          onClick={onReset}
            className="text-xs text-jamaica-green hover:text-jamaica-green/90 hover:bg-jamaica-green/10 h-8 px-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105"
        >
            Clear all
        </Button>
        )}
      </div>
      
      {/* Enhanced Price Range Filter */}
      <Collapsible defaultOpen className="border-b border-gray-100/80 pb-6">
        <CollapsibleTrigger className="flex justify-between items-center w-full py-3 group hover:bg-gray-50/50 rounded-lg px-2 transition-all duration-300">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-jamaica-green/20 to-jamaica-green/10 rounded-lg flex items-center justify-center group-hover:from-jamaica-green/30 group-hover:to-jamaica-green/20 transition-all duration-300">
              <Banknote className="h-4 w-4 text-jamaica-green" />
            </div>
            <h4 className="text-sm font-semibold text-gray-700 group-hover:text-jamaica-green transition-colors duration-300">
              Price Range
            </h4>
            {isPriceFilterActive && (
              <Badge className="bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white border-none text-xs shadow-sm animate-pulse">Active</Badge>
            )}
          </div>
          <ChevronDown className="h-4 w-4 text-gray-400 group-hover:text-jamaica-green transition-all duration-300 group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4 px-2">
          <div className="space-y-5">
            <Slider 
              defaultValue={[priceRange[0], priceRange[1]]} 
              min={0}
              max={100}
              step={5}
              value={[localPriceRange[0], localPriceRange[1]]}
              onValueChange={(value) => setLocalPriceRange([value[0], value[1]])}
              className="my-6" 
            />
            <div className="flex items-center justify-between gap-3">
              <div className="bg-gradient-to-r from-gray-50 to-white px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 border border-gray-200 shadow-sm flex-1 text-center">
                {formatPrice(localPriceRange[0])}
              </div>
              <div className="w-4 h-px bg-gray-300"></div>
              <div className="bg-gradient-to-r from-gray-50 to-white px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-700 border border-gray-200 shadow-sm flex-1 text-center">
                {formatPrice(localPriceRange[1])}
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
      
      {/* Enhanced Categories Filter */}
      <Collapsible defaultOpen className="border-b border-gray-100/80 pb-6">
        <CollapsibleTrigger className="flex justify-between items-center w-full py-3 group hover:bg-gray-50/50 rounded-lg px-2 transition-all duration-300">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-jamaica-green/20 to-jamaica-green/10 rounded-lg flex items-center justify-center group-hover:from-jamaica-green/30 group-hover:to-jamaica-green/20 transition-all duration-300">
              <Tag className="h-4 w-4 text-jamaica-green" />
            </div>
            <h4 className="text-sm font-semibold text-gray-700 group-hover:text-jamaica-green transition-colors duration-300">
              Categories
            </h4>
            {selectedCategories.length > 0 && (
              <Badge className="bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white border-none text-xs shadow-sm">{selectedCategories.length}</Badge>
            )}
          </div>
          <ChevronDown className="h-4 w-4 text-gray-400 group-hover:text-jamaica-green transition-all duration-300 group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4 px-2">
          <div className="space-y-3">
            {categoriesLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin text-jamaica-green mr-2" />
                <span className="text-sm text-gray-600">Loading categories...</span>
              </div>
            ) : categoriesError ? (
              <div className="text-center py-4">
                <div className="flex items-center justify-center mb-2">
                  <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                  <span className="text-sm text-red-600">{categoriesError}</span>
                </div>
              </div>
            ) : availableCategories.length > 0 ? (
              availableCategories.map((cat) => (
                <div key={cat} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50/50 transition-all duration-300 group">
                  <Checkbox 
                    id={`cat-${cat}`} 
                    checked={selectedCategories.includes(cat)}
                    onCheckedChange={() => handleCategoryChange(cat)}
                    className="text-jamaica-green border-gray-300 rounded-md data-[state=checked]:bg-jamaica-green data-[state=checked]:border-jamaica-green shadow-sm"
                  />
                  <Label 
                    htmlFor={`cat-${cat}`}
                    className="text-sm cursor-pointer text-gray-600 hover:text-gray-900 transition-colors duration-300 font-medium group-hover:text-jamaica-green flex-1"
                  >
                    {cat.toUpperCase()}
                  </Label>
                </div>
              ))
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500">No categories available</p>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Enhanced Brands Filter */}
      <Collapsible defaultOpen className="border-b border-gray-100/80 pb-6">
        <CollapsibleTrigger className="flex justify-between items-center w-full py-3 group hover:bg-gray-50/50 rounded-lg px-2 transition-all duration-300">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-jamaica-yellow/20 to-jamaica-yellow/10 rounded-lg flex items-center justify-center group-hover:from-jamaica-yellow/30 group-hover:to-jamaica-yellow/20 transition-all duration-300">
              <Tag className="h-4 w-4 text-jamaica-yellow/80" />
            </div>
            <h4 className="text-sm font-semibold text-gray-700 group-hover:text-jamaica-green transition-colors duration-300">
              Brands
            </h4>
            {selectedBrands.length > 0 && (
              <Badge className="bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white border-none text-xs shadow-sm">{selectedBrands.length}</Badge>
            )}
          </div>
          <ChevronDown className="h-4 w-4 text-gray-400 group-hover:text-jamaica-green transition-all duration-300 group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-4 px-2">
          <div className="space-y-3">
            {brandsLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin text-jamaica-green mr-2" />
                <span className="text-sm text-gray-600">Loading brands...</span>
              </div>
            ) : brandsError ? (
              <div className="text-center py-4">
                <div className="flex items-center justify-center mb-2">
                  <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                  <span className="text-sm text-red-600">{brandsError}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetryBrands}
                  className="text-xs border-jamaica-green text-jamaica-green hover:bg-jamaica-green/5"
                >
                  Retry
                </Button>
              </div>
            ) : availableBrands.length > 0 ? (
              availableBrands.map((brand) => (
                <div key={brand} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50/50 transition-all duration-300 group">
                  <Checkbox 
                    id={brand} 
                    checked={selectedBrands.includes(brand)}
                    onCheckedChange={() => handleBrandChange(brand)}
                    className="text-jamaica-green border-gray-300 rounded-md data-[state=checked]:bg-jamaica-green data-[state=checked]:border-jamaica-green shadow-sm"
                  />
                  <Label 
                    htmlFor={brand}
                    className="text-sm cursor-pointer text-gray-600 hover:text-gray-900 transition-colors duration-300 font-medium group-hover:text-jamaica-green flex-1"
                  >
                    {brand}
                  </Label>
                </div>
              ))
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500">No brands available</p>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default ProductFilters;
